import { useState } from "react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { FiChevronDown, FiLoader } from "react-icons/fi";

import { useGetMarketAreasForFilterQuery } from "gql/generated.graphql";
import { SearchBarFiltersProps } from "../../constants/constants";
import CustomMultiSelect from "./custom-multi-select";
import { handleGoToFirstPage } from "../../helpers/idx-common-helpers";

export const MarketAreaFilter = ({
  filters,
  mainColor,
  mainColorLight,
  textColor,
  isMobile = false,
  setFilters,
  goToFirstPage,
}: SearchBarFiltersProps) => {
  const [marketAreaQuery, setMarketAreaQuery] = useState<string>("");
  const [marketAreaPagination, setMarketAreaPagination] = useState<number>(10);
  const getMarketAreasQuery = useGetMarketAreasForFilterQuery({
    variables: {
      input: {
        searchText: marketAreaQuery,
        pagination: { limit: marketAreaQuery ? null : marketAreaPagination },
      },
    },
  });
  const marketAreas = getMarketAreasQuery.data?.getMarketAreasForFilter || [];

  const addMarketAreaFilter = (id: string) => {
    setFilters((draft) => {
      const index = draft.marketAreaIds.findIndex((item) => item === id);
      if (index === -1) {
        draft.marketAreaIds.push(id);
        return;
      }
      draft.marketAreaIds.splice(index, 1);
    });

    handleGoToFirstPage(isMobile, goToFirstPage);
  };

  const handleMobileSelectChange = (values: string[]) => {
    setFilters((draft) => {
      draft.marketAreaIds = values.map((value) => value);
    });
  };

  const getMobileSelectOptions = () => {
    return marketAreas.map((area) => ({
      value: area._id,
      label: area.name,
    }));
  };

  const handleMobileSearch = (query: string) => {
    setMarketAreaQuery(query);
    setMarketAreaPagination(10);
  };

  return isMobile ? (
    <>
      <CustomMultiSelect
        options={getMobileSelectOptions()}
        value={filters.marketAreaIds}
        onChange={handleMobileSelectChange}
        onSearch={handleMobileSearch}
        onLoadMore={() =>
          setMarketAreaPagination((currentValue) => currentValue + 20)
        }
        isLoading={getMarketAreasQuery.loading}
        placeholder="Select market areas"
        className="w-full"
      />
    </>
  ) : (
    <Popover>
      <PopoverButton
        style={{
          backgroundColor: !!filters.marketAreaIds?.length
            ? mainColor
            : mainColorLight,
          color: !!filters.marketAreaIds?.length ? "white" : mainColor,
        }}
        className={`text-sm flex gap-2 items-center rounded-full py-2 px-4 hover:brightness-75`}
      >
        <span className="">
          Market Area{" "}
          {!!filters.marketAreaIds?.length &&
            `(${filters.marketAreaIds?.length})`}
        </span>
        <FiChevronDown />
      </PopoverButton>
      <PopoverPanel
        anchor="bottom end"
        className="bg-white shadow rounded-lg mt-1"
        style={{ color: textColor }}
      >
        <input
          placeholder="Type to filter areas"
          className="m-4 mb-2 rounded-lg border border-slate-300 px-4 py-2 flex-1 focus:outline-none"
          value={marketAreaQuery}
          onChange={(event) => setMarketAreaQuery(event.target.value)}
        ></input>
        <div className="flex flex-col max-h-[480px] overflow-auto">
          {getMarketAreasQuery.loading && (
            <div className="h-40 animate-pulse flex items-center justify-center">
              <FiLoader className="text-lg animate-spin" />
            </div>
          )}
          {!getMarketAreasQuery.loading && !marketAreas.length && (
            <div className="h-24 flex items-center justify-center">
              <p>No market areas found.</p>
            </div>
          )}
          {marketAreas.map((marketArea) => (
            <button
              className="flex items-center gap-2 px-6 py-2 hover:font-bold"
              key={marketArea._id}
              value={marketArea._id}
              onClick={() => addMarketAreaFilter(marketArea._id)}
            >
              <input
                type="checkbox"
                checked={
                  !!filters.marketAreaIds.find(
                    (item) => item === marketArea._id
                  )
                }
                readOnly
              />
              <span>{marketArea.name}</span>
            </button>
          ))}
          {!getMarketAreasQuery.loading &&
            !!marketAreas.length &&
            marketAreas.length >= 10 &&
            marketAreas.length % 10 === 0 && (
              <button
                className="py-2 px-4 flex items-center justify-center w-full hover:bg-slate-200"
                onClick={() =>
                  setMarketAreaPagination((currentValue) => currentValue + 20)
                }
              >
                See more
              </button>
            )}
        </div>
      </PopoverPanel>
    </Popover>
  );
};
